import {OrderRequestEntity, PlaneEntity} from "../../../proto/generated/api_entities_pb";
import {
  IFreighterOrderFromOrderRequest,
  IFreighterOrderRequest,
  IFreighterOrderRouteOrderRequest,
  IOrderRequestPlane
} from "./freighterRequestsReducerTypes";
import {isValidDate, parseStringAsUTCDate} from "../../../utils/helpers/dateHelpers/dateHelpers";
import {OrderRequestStatusEnum} from "../../../utils/constans/statuses/orderRequestStatuses";
import {
  mapAirportEntityToIAirport,
  mapPlaneCategoryEntityToIPlaneCategory,
  mapPlaneEntityToIPlane
} from "../fleetReducer/fleetMappers";
import {IAirport, IPlane} from "../fleetReducer/fleetReducerTypes";
import {mapUserEntityToIUser} from "../userReducer/userMappers";
import {mapCompanyEntityToICompany} from "../companyReducer/companyMappers";
import {mapUserEntityToIManager} from "../personnelManagementReducer/personnelManagementMappers";
import {calculateArrivalDate} from "../../../utils/helpers/dateHelpers/calculateArrivalDate";
import {OrderTypeEnum} from "../chartererRequestsReducer/chartererRequestsReducerTypes";
import {Logger} from "../../../utils/logger/Logger";


/**
 * Maps PlaneEntity to IPlane with defensive handling for missing baseAirport.
 * Used in orderRequest context where baseAirport might not be fully populated.
 * 
 * NOTE FOR BACKEND: PlaneEntity.getBaseairport() should always be populated
 * in OrderRequestEntity.planesList[].plane to enable positioning flight display.
 */
const mapPlaneEntityToIPlaneDefensive = (plane: PlaneEntity): IPlane | null => {
  try {
    return mapPlaneEntityToIPlane(plane);
  } catch (error) {
    const baseAirport = plane.getBaseairport();
    if (!baseAirport) {
      Logger.error(`PlaneEntity missing baseAirport (planeId: ${plane.getPlaneid()}). Backend should populate PlaneEntity.getBaseairport() in OrderRequestEntity.planesList for positioning flights.`);
      // Return null - the plane won't be included but order will still be mapped
      // Backend should fix this by including baseAirport in the API response
      return null;
    }
    // If baseAirport exists but mapping still fails, re-throw the error
    throw error;
  }
};


export const mapOrderRequestEntityToIFreighterOrderRequest = (item: OrderRequestEntity): IFreighterOrderRequest => {
  const company = item.getFlycompany();
  if (!company)
    throw new Error("Null CompanyEntity (flyCompany) in received OrderRequestEntity");

  const user = item.getFreighter();
  if (!user)
    throw new Error("Null UserEntity (freighter) in received OrderRequestEntity");

  const createdAtDate = parseStringAsUTCDate(item.getCreatedat())
  if (!isValidDate(createdAtDate))
    throw new Error(`Invalid createdAtDate in received OrderRequestEntity, ${item.getCreatedat()}`);

  const plane = item.getPlane();
  //TODO ask backend to receive undefined or existing plane
  const isPlaneOffered = !!plane && plane.getPlaneid() !== 0;

  const currency = item.getCurrency();
  if (isPlaneOffered && !currency)
    throw new Error("Null CurrencyEntity (currency) in received OrderRequestEntity");

  const orderFromOrderRequest = item.getOrder();
  if (!orderFromOrderRequest)
    throw new Error("Null OrderEntity (order) in received OrderRequestEntity");

  const planeCategories = orderFromOrderRequest.getPlanecategoriesList().map(mapPlaneCategoryEntityToIPlaneCategory);

  const orderUser = orderFromOrderRequest.getUser();
  if (!orderUser)
    throw new Error("Null UserEntity (user) in received OrderRequestEntity > OrderEntity");

  const orderCompany = orderUser.getCompany();
  if (!orderCompany)
    throw new Error("Null CompanyEntity (company) in received OrderRequestEntity > OrderEntity > UserEntity");

  const orderCreatedAtDate = parseStringAsUTCDate(orderFromOrderRequest.getCreatedat())
  if (!isValidDate(orderCreatedAtDate))
    throw new Error(`Invalid createdAt in received OrderRequestEntity, ${orderFromOrderRequest.getCreatedat()}`);

  let orderRouteOrderRequests: IFreighterOrderRouteOrderRequest[] = item.getOrderrouteorderrequestsList().map((orderRouteOrderRequest, index) => {
    // Always use the full orderRoute from order's routes list for complete data (airports, etc.)
    // But get the departure date from orderRouteOrderRequest.getOrderroute() if available (actual date)
    const orderRouteFromRequest = orderRouteOrderRequest.getOrderroute();
    const orderRoutesList = orderFromOrderRequest.getOrderroutesList();
    
    // Find the matching orderRoute from order's routes list (has complete data)
    let orderRoute = orderRouteFromRequest 
      ? orderRoutesList.find(or => or.getOrderrouteid() === orderRouteFromRequest.getOrderrouteid())
      : null;
    
    // If not found by ID, try matching by index
    if (!orderRoute && index >= 0 && index < orderRoutesList.length) {
      orderRoute = orderRoutesList[index];
    }
    
    if (!orderRoute) {
      throw new Error("Null OrderRouteEntity (orderRoute) in received OrderRequestEntity");
    }

    // Get airports from the full orderRoute (always has complete data)
    const orderRouteAirportFrom = orderRoute.getAirportfrom();
    if (!orderRouteAirportFrom)
      throw new Error("Null AirportEntity (airportFrom) in received OrderRequestEntity > OrderRouteEntity");
    const orderRouteAirportFromMapped = mapAirportEntityToIAirport(orderRouteAirportFrom);

    const orderRouteAirportTo = orderRoute.getAirportto();
    if (!orderRouteAirportTo)
      throw new Error("Null AirportEntity (airportTo) in received OrderRequestEntity > OrderRouteEntity");
    const orderRouteAirportToMapped = mapAirportEntityToIAirport(orderRouteAirportTo);

    // Use departure date from orderRouteOrderRequest.orderRoute if available (actual date), otherwise from order's route
    // Priority: orderRouteOrderRequest.orderRoute.departuredate (actual date) > orderRoute.departuredate (requested date)
    let departureDate: Date;
    if (orderRouteFromRequest) {
      const departureDateStr = orderRouteFromRequest.getDeparturedate();
      if (departureDateStr) {
        try {
          departureDate = parseStringAsUTCDate(departureDateStr);
          if (!isValidDate(departureDate)) {
            // If date is invalid, fall back to order route's departure date
            departureDate = parseStringAsUTCDate(orderRoute.getDeparturedate());
          }
        } catch (e) {
          // If parsing fails, fall back to order route's departure date
          departureDate = parseStringAsUTCDate(orderRoute.getDeparturedate());
        }
      } else {
        // If no departure date in orderRouteFromRequest, use order route's departure date
        departureDate = parseStringAsUTCDate(orderRoute.getDeparturedate());
      }
    } else {
      // If orderRouteFromRequest is null, use order route's departure date
      departureDate = parseStringAsUTCDate(orderRoute.getDeparturedate());
    }
    
    if (!isValidDate(departureDate))
      throw new Error(`Invalid departureDate in received OrderRequestEntity, ${orderRoute.getDeparturedate()}`);

    const flyHours = Math.floor(orderRouteOrderRequest.getFlyduration() / 60);
    const flyMinutes = orderRouteOrderRequest.getFlyduration() % 60;

    // Use backend-calculated arrivalDate if available, otherwise calculate it
    let arrivalDate: Date;
    const backendArrivalDate = orderRouteOrderRequest.getArrivaldate();
    if (backendArrivalDate) {
      try {
        arrivalDate = parseStringAsUTCDate(backendArrivalDate);
        if (!isValidDate(arrivalDate)) {
          // Fallback to calculation if backend date is invalid
          arrivalDate = calculateArrivalDate(orderRouteAirportFromMapped, orderRouteAirportToMapped, departureDate, orderRouteOrderRequest.getFlyduration() + orderRouteOrderRequest.getTechnicalstopduration());
        }
      } catch (e) {
        // Fallback to calculation if parsing fails
        arrivalDate = calculateArrivalDate(orderRouteAirportFromMapped, orderRouteAirportToMapped, departureDate, orderRouteOrderRequest.getFlyduration() + orderRouteOrderRequest.getTechnicalstopduration());
      }
    } else {
      // Fallback to calculation if backend doesn't provide arrivalDate
      arrivalDate = calculateArrivalDate(orderRouteAirportFromMapped, orderRouteAirportToMapped, departureDate, orderRouteOrderRequest.getFlyduration() + orderRouteOrderRequest.getTechnicalstopduration());
    }
    
    if (isPlaneOffered && !isValidDate(arrivalDate))
      throw new Error(`Invalid arrivalDate`);

    return {
      orderRouteOrderRequestId: orderRouteOrderRequest.getOrderrouteorderrequestid(),
      orderRequestId: item.getOrderrequestid(),
      orderRouteId: orderRoute.getOrderrouteid(),
      flyHours: flyHours,
      flyMinutes: flyMinutes,
      flyDistance: orderRouteOrderRequest.getFlydistance(),
      technicalStopDuration: orderRouteOrderRequest.getTechnicalstopduration(),
      technicalStopCount: orderRouteOrderRequest.getTechnicalstopcount(),
      arrivalDate: arrivalDate,
      orderRoute: {
        id: orderRoute.getOrderrouteid(),
        airportFrom: orderRouteAirportFromMapped,
        airportTo: orderRouteAirportToMapped,
        departureDate: departureDate,
        pax: orderRoute.getPax(),
      }
    }
  })

  if (orderRouteOrderRequests.length === 0) {
    orderRouteOrderRequests = orderFromOrderRequest.getOrderroutesList().map(orderRoute => {
      const orderRouteAirportFrom = orderRoute.getAirportfrom();
      if (!orderRouteAirportFrom)
        throw new Error("Null AirportEntity (airportFrom) in received OrderRequestEntity > OrderEntity > OrderRouteEntity");
      const orderRouteAirportFromMapped = mapAirportEntityToIAirport(orderRouteAirportFrom);

      const orderRouteAirportTo = orderRoute.getAirportto();
      if (!orderRouteAirportTo)
        throw new Error("Null AirportEntity (airportTo) in received OrderRequestEntity > OrderEntity > OrderRouteEntity");
      const orderRouteAirportToMapped = mapAirportEntityToIAirport(orderRouteAirportTo);

      const departureDate = parseStringAsUTCDate(orderRoute.getDeparturedate())
      if (!isValidDate(departureDate))
        throw new Error(`Invalid departureDate in received OrderRequestEntity > OrderEntity > OrderRouteEntity, ${orderRoute.getDeparturedate()}`);

      return {
        orderRouteOrderRequestId: undefined,
        orderRequestId: item.getOrderrequestid(),
        orderRouteId: orderRoute.getOrderrouteid(),
        flyHours: undefined,
        flyMinutes: undefined,
        flyDistance: undefined,
        technicalStopDuration: undefined,
        technicalStopCount: undefined,
        arrivalDate: undefined,
        orderRoute: {
          id: orderRoute.getOrderrouteid(),
          airportFrom: orderRouteAirportFromMapped,
          airportTo: orderRouteAirportToMapped,
          departureDate: departureDate,
          pax: orderRoute.getPax(),
        }
      }
    })
  }

  // Map order routes
  const orderRoutes: IFreighterOrderRoute[] = orderFromOrderRequest.getOrderroutesList().map(orderRoute => {
    const orderRouteAirportFrom = orderRoute.getAirportfrom();
    if (!orderRouteAirportFrom)
      throw new Error("Null AirportEntity (airportFrom) in received OrderRequestEntity > OrderEntity > OrderRouteEntity");
    const orderRouteAirportFromMapped = mapAirportEntityToIAirport(orderRouteAirportFrom);

    const orderRouteAirportTo = orderRoute.getAirportto();
    if (!orderRouteAirportTo)
      throw new Error("Null AirportEntity (airportTo) in received OrderRequestEntity > OrderEntity > OrderRouteEntity");
    const orderRouteAirportToMapped = mapAirportEntityToIAirport(orderRouteAirportTo);

    const departureDate = parseStringAsUTCDate(orderRoute.getDeparturedate())
    if (!isValidDate(departureDate))
      throw new Error(`Invalid departureDate in received OrderRequestEntity > OrderEntity > OrderRouteEntity, ${orderRoute.getDeparturedate()}`);

    return {
      id: orderRoute.getOrderrouteid(),
      airportFrom: orderRouteAirportFromMapped,
      airportTo: orderRouteAirportToMapped,
      departureDate: departureDate,
      pax: orderRoute.getPax(),
    };
  });

  const order: IFreighterOrderFromOrderRequest = {
    orderId: orderFromOrderRequest.getOrderid(),
    type: orderFromOrderRequest.getType() as OrderTypeEnum,
    status: orderFromOrderRequest.getOrderstatusid(),
    manager: mapUserEntityToIManager(orderUser),
    company: mapCompanyEntityToICompany(orderCompany),
    createdDate: orderCreatedAtDate,
    planeCategories: planeCategories,
    companionsAllowed: orderFromOrderRequest.getCompanionsallowed(),
    comment: orderFromOrderRequest.getComment(),
    isArchived: orderFromOrderRequest.getArchived(),
    orderRoutes: orderRoutes,
  }

  // Маппинг planeList из OrderRequestEntity
  // NOTE FOR BACKEND: PlaneEntity.getBaseairport() must be populated in OrderRequestEntity.planesList[].plane
  // to enable positioning flight display in the operator interface
  const planeList: IOrderRequestPlane[] = item.getPlanesList()
    .map(planeEntity => {
      const plane = planeEntity.getPlane();
      if (!plane) {
        throw new Error("Null PlaneEntity in OrderRequestPlaneEntity");
      }

      const currency = planeEntity.getCurrency();
      const operatorCurrency = planeEntity.getOperatorcurrency();

      // Map plane with defensive handling - returns null if baseAirport is missing
      const mappedPlane = mapPlaneEntityToIPlaneDefensive(plane);
      if (!mappedPlane) {
        // Skip this plane if mapping failed due to missing baseAirport
        // Backend should include baseAirport in PlaneEntity.getBaseairport()
        return null;
      }

      return {
        orderRequestPlaneId: planeEntity.getOrderrequestplaneid(),
        orderRequestId: planeEntity.getOrderrequestid(),
        planeId: planeEntity.getPlaneid(),
        plane: mappedPlane,
      orderRequestStatusId: planeEntity.getOrderrequeststatusid(),
      operatorPrice: planeEntity.getOperatorprice(),
      operatorCurrencyId: planeEntity.getOperatorcurrencyid(),
      currency: currency ? {
        id: currency.getCurrencyid(),
        name: currency.getName(),
      } : undefined,
      operatorCurrency: operatorCurrency ? {
        id: operatorCurrency.getCurrencyid(),
        name: operatorCurrency.getName(),
      } : undefined,
      preliminaryPrice: planeEntity.getPreliminaryprice(),
      positioningDurationMinutes: planeEntity.getPositioningdurationminutes(),
      positioningDistanceKm: planeEntity.getPositioningdistancekm(),
      // NOTE: Backend should add positioningFromAirportId to OrderRequestPlaneEntity in protobuf
      // For now, try to get it if the method exists (it might be in BrokerAircraftEntity but not OrderRequestPlaneEntity yet)
      positioningFromAirportId: (typeof (planeEntity as any).getPositioningfromairportid === 'function' && (planeEntity as any).getPositioningfromairportid() > 0) 
        ? (planeEntity as any).getPositioningfromairportid() 
        : undefined,
      positioningDepartureDate: (typeof (planeEntity as any).getPositioningdeparturedate === 'function') 
        ? (planeEntity as any).getPositioningdeparturedate() || undefined
        : undefined,
      flightDurationMinutes: planeEntity.getFlightdurationminutes(),
      flightDistanceKm: planeEntity.getFlightdistancekm(),
      refuelingCount: planeEntity.getRefuelingcount(),
      availableFromDate: planeEntity.getAvailablefromdate(),
      availableToDate: planeEntity.getAvailabletodate(),
      arrivalDate: planeEntity.getArrivaldate(),
      departureDate: planeEntity.getDeparturedate(),
    };
    })
    .filter((plane): plane is IOrderRequestPlane => plane !== null); // Filter out null planes

  return {
    id: item.getOrderrequestid(),
    user: mapUserEntityToIUser(user),
    company: mapCompanyEntityToICompany(company),
    status: item.getOrderrequeststatusid() as OrderRequestStatusEnum,
    plane: isPlaneOffered ? mapPlaneEntityToIPlane(plane!) : undefined,
    price: isPlaneOffered ? item.getPrice() : undefined,
    currency: isPlaneOffered && currency ? {
      id: currency.getCurrencyid(),
      name: currency.getName(),
    } : undefined,
    dateCreated: createdAtDate,
    isArchived: item.getArchived(),
    isPlaneOffered: isPlaneOffered,
    isFavorite: item.getIsfavorite(),
    order: order,
    orderRouteOrderRequests: orderRouteOrderRequests,
    planeList: planeList.length > 0 ? planeList : undefined,
  }
}